// Pure geometry shared by quotation, cutting demand and canonical validation.
const positive = value => Number.isFinite(Number(value)) && Number(value) > 0 ? Number(value) : 0;
export function repeatCutHeight(baseCutHeight, repeat = {}) {
  const base = positive(baseCutHeight), interval = positive(repeat.verticalRepeatInch);
  if (!repeat.verticalEnabled || !interval || !base) return base;
  const ratio = base / interval;
  // Only tolerate floating-point representation error, not a real short cut.
  return Math.ceil(ratio - Number.EPSILON * Math.max(1, Math.abs(ratio)) * 4) * interval;
}

export function horizontalPatternRules(rules = {}, repeat = {}) {
  const width = Math.max(1, positive(rules.fabricWidthIn));
  const usable = Math.max(1, Number(rules.usableFabricWidthIn ?? width - Number(rules.usableWidthDeductionIn || 0)));
  const interval = positive(repeat.horizontalRepeatInch);
  const active = Boolean(repeat.horizontalEnabled && interval);
  const unsupportedOrientation = !['', 'vertical', 'standard'].includes(String(rules.orientation || '').toLowerCase());
  const count = active ? Math.floor(usable / interval + Number.EPSILON * Math.max(1, usable / interval) * 4) : 0;
  const manual = active && (count < 1 || unsupportedOrientation);
  const aligned = active && !manual ? Math.min(usable, count * interval) : usable;
  return { ...rules, usableFabricWidthIn: aligned, usableWidthDeductionIn: width - aligned,
    horizontalRepeatAdjusted: aligned < usable, baseUsableFabricWidthIn: usable,
    horizontalRepeatInch: interval, repeatSettings: repeat,
    horizontalPatternMatchingStatus: !active ? 'not_requested' : manual ? 'manual_review' : 'conservative_equal_repeat_width',
    horizontalPatternMatchingNote: !active ? '' : manual
      ? 'Horizontal matching needs manual review: repeat exceeds usable width or fabric orientation is unsupported.'
      : 'Conservative equal repeat-width joins; pattern phase, selvedge origin and seam placement must be checked before cutting.' };
}

export const PATTERN_REPEAT_VERSION = 'pattern-repeat-v2';
export function cuttingDropMetres(cutHeightIn, repeat = {}, frozen = false, version = '') {
  const inches = positive(cutHeightIn);
  // Existing no-repeat/frozen billing is not silently repriced. Explicit pattern
  // calculations retain SI precision until final purchase/billing rounding.
  return (!frozen || version === PATTERN_REPEAT_VERSION) && ((repeat.verticalEnabled && positive(repeat.verticalRepeatInch)) || (repeat.horizontalEnabled && positive(repeat.horizontalRepeatInch)))
    ? inches * 0.0254 : Math.round((inches / 39.37) * 1000) / 1000;
}

export function applyRepeatEditToRows(rows, key, nextRepeat, { getFabricKey, appliesToRow }) {
  return rows.map(row => getFabricKey(row) === key && appliesToRow(row)
    ? { ...row, repeatSettings: nextRepeat, calculationSnapshot: null, calculationSnapshotStatus: 'current', __forceCurrentCalculation: true, __recalculated: true }
    : row);
}

export function patternCuttingInstructions(calc = {}) {
  const repeat = calc.repeatSettings || {};
  const rules = calc.calculationBreakdown?.fabricRules || {};
  const lines = [];
  if (repeat.verticalEnabled && positive(repeat.verticalRepeatInch)) {
    if (positive(calc.baseCutHeightIn)) lines.push(`Base cut: ${Number(calc.baseCutHeightIn).toFixed(2)} in`);
    lines.push(`VR-aligned cut: ${Number(calc.cutCurtain || calc.cutRoman || 0).toFixed(2)} in`);
  }
  if (repeat.horizontalEnabled && positive(repeat.horizontalRepeatInch)) lines.push(rules.horizontalPatternMatchingNote ||
    'Conservative equal repeat-width joins; pattern phase and seam placement need cutting review.');
  return lines.join('; ');
}
